import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Search,
  Upload,
  FileText,
  Users,
  Edit,
  Trash2,
  Eye,
  Loader,
  AlertCircle,
  XCircle,
  CheckCircle,
  X,
  Download,
  Clock,
} from "lucide-react";
import {
  getSegments,
  createSegment,
  deleteSegment,
  uploadContacts,
  getContacts,
} from "../apis/segments";
import { toast } from "react-toastify";

const ContactLists = () => {
  const [segments, setSegments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    segment_name: "",
    description: "",
  });
  const [csvFile, setCsvFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    setIsLoading(true);
    setError("");
    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await getSegments(authToken);
      setSegments(response.data.segments || []);
    } catch (err) {
      console.error("Error fetching segments:", err);
      setError(err.response?.data?.error || "Failed to load contact lists");
      toast.error(err.response?.data?.error || "Failed to load contact lists");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      { contact_name: "John Doe", email: "john.doe@example.com", phone: "+1234567890" },
      { contact_name: "Jane Smith", email: "jane.smith@example.com", phone: "+1234567891" },
      { contact_name: "Bob Johnson", email: "bob.johnson@example.com", phone: "+1234567892" },
    ];

    // Convert to CSV
    const headers = ["contact_name", "email", "phone"];
    const csvContent = [
      headers.join(","),
      ...sampleData.map((row) =>
        headers.map((header) => `"${row[header] || ""}"`).join(",")
      ),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "sample_contacts.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreateSegment = async (e) => {
    e.preventDefault();
    if (!formData.segment_name.trim()) {
      toast.error("Segment name is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const authToken = localStorage.getItem("userAccessToken");
      
      // First create the segment
      const createResponse = await createSegment(authToken, formData);
      const newSegmentId = createResponse.data.segment.segment_id;
      
      // If CSV file is provided, upload it
      if (csvFile) {
        try {
          const uploadResponse = await uploadContacts(authToken, newSegmentId, csvFile);
          
          // Show summary message
          const total = uploadResponse.data.total || uploadResponse.data.inserted || 0;
          const invalid = uploadResponse.data.invalid || 0;
          const duplicates = uploadResponse.data.duplicates || 0;
          
          let message = `Contact list created. ${total} number(s) uploaded`;
          if (invalid > 0) {
            message += `, ${invalid} invalid number(s)`;
          }
          if (duplicates > 0) {
            message += `. ${duplicates} duplicate number(s) skipped.`;
          } else {
            message += `.`;
          }
          
          if (invalid > 0 || duplicates > 0) {
            toast.warning(message);
          } else {
            toast.success(message);
          }
        } catch (uploadErr) {
          console.error("Error uploading contacts:", uploadErr);
          toast.warning("Contact list created but failed to upload contacts. You can upload them later.");
        }
      } else {
        toast.success("Contact list created successfully");
      }
      
      setShowCreateModal(false);
      setFormData({ segment_name: "", description: "" });
      setCsvFile(null);
      await fetchSegments();
    } catch (err) {
      console.error("Error creating segment:", err);
      toast.error(err.response?.data?.error || "Failed to create contact list");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSegment = async (segmentId) => {
    if (!window.confirm("Are you sure you want to delete this contact list? All contacts will also be deleted.")) {
      return;
    }

    try {
      const authToken = localStorage.getItem("userAccessToken");
      await deleteSegment(authToken, segmentId);
      toast.success("Contact list deleted successfully");
      await fetchSegments();
    } catch (err) {
      console.error("Error deleting segment:", err);
      toast.error(err.response?.data?.error || "Failed to delete contact list");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        setCsvFile(file);
      } else {
        toast.error("Please select a CSV file");
        e.target.value = "";
      }
    }
  };

  const handleUploadContacts = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      toast.error("Please select a CSV file");
      return;
    }

    setIsSubmitting(true);
    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await uploadContacts(
        authToken,
        selectedSegment.segment_id,
        csvFile
      );
      
      // Show summary message with validation and duplicate info
      const total = response.data.total || response.data.inserted || 0;
      const invalid = response.data.invalid || 0;
      const duplicates = response.data.duplicates || 0;
      
      let message = `${total} number(s) uploaded`;
      if (invalid > 0) {
        message += `, ${invalid} invalid number(s)`;
      }
      if (duplicates > 0) {
        message += `. ${duplicates} duplicate number(s) skipped.`;
      } else {
        message += `.`;
      }
      
      if (invalid > 0 || duplicates > 0) {
        toast.warning(message);
      } else {
        toast.success(message);
      }
      
      setShowUploadModal(false);
      setCsvFile(null);
      await fetchSegments();
    } catch (err) {
      console.error("Error uploading contacts:", err);
      toast.error(err.response?.data?.error || "Failed to upload contacts");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewContacts = async (segment) => {
    setSelectedSegment(segment);
    setShowContactsModal(true);
    setIsLoadingContacts(true);
    setContacts([]);

    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await getContacts(authToken, segment.segment_id);
      setContacts(response.data.contacts || []);
    } catch (err) {
      console.error("Error fetching contacts:", err);
      toast.error(err.response?.data?.error || "Failed to load contacts");
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const handleDownloadContacts = async (segment) => {
    try {
      const authToken = localStorage.getItem("userAccessToken");
      
      // Fetch all contacts (use a large limit to get all)
      const response = await getContacts(authToken, segment.segment_id, {
        limit: 10000,
        offset: 0,
      });
      
      const contactsToDownload = response.data.contacts || [];
      
      if (contactsToDownload.length === 0) {
        toast.info("No contacts to download");
        return;
      }

      // Convert to CSV
      const headers = ["contact_name", "email", "phone", "status"];
      const csvContent = [
        headers.join(","),
        ...contactsToDownload.map((contact) =>
          headers
            .map((header) => {
              const value = contact[header] || "";
              // Escape quotes and wrap in quotes if contains comma or newline
              return `"${value.toString().replace(/"/g, '""')}"`;
            })
            .join(",")
        ),
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${segment.segment_name.replace(/[^a-z0-9]/gi, "_")}_contacts.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Downloaded ${contactsToDownload.length} contacts`);
    } catch (err) {
      console.error("Error downloading contacts:", err);
      toast.error(err.response?.data?.error || "Failed to download contacts");
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      active: {
        icon: CheckCircle,
        label: "Active",
        class: "bg-green-50 text-green-700 border-green-200",
      },
      inactive: {
        icon: XCircle,
        label: "Inactive",
        class: "bg-red-50 text-red-700 border-red-200",
      },
    };
    return configs[status] || configs.active;
  };

  const filteredSegments = segments.filter((segment) => {
    const matchesSearch =
      segment.segment_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      segment.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" || segment.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-5 py-4">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                Contact Lists
              </h1>
              <p className="text-xs text-gray-600 mt-0.5">
                Create and manage your contact lists for campaigns
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-semibold rounded-lg transition-all shadow-sm"
            >
              <Plus size={16} />
              New Contact List
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-600 mb-0.5">
                    Total Lists
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {segments.length}
                  </p>
                </div>
                <div className="p-2 bg-blue-50 rounded-lg">
                  <FileText className="text-blue-600" size={18} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-600 mb-0.5">Active</p>
                  <p className="text-xl font-bold text-green-600">
                    {segments.filter((s) => s.status === "active").length}
                  </p>
                </div>
                <div className="p-2 bg-green-50 rounded-lg">
                  <CheckCircle className="text-green-600" size={18} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-600 mb-0.5">
                    Total Contacts
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {segments.reduce(
                      (sum, s) => sum + (parseInt(s.contact_count) || 0),
                      0
                    )}
                  </p>
                </div>
                <div className="p-2 bg-purple-50 rounded-lg">
                  <Users className="text-purple-600" size={18} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2"
          >
            <AlertCircle className="text-red-600" size={16} />
            <div className="flex-1">
              <p className="text-xs text-red-700 font-medium">{error}</p>
            </div>
            <button
              onClick={() => setError("")}
              className="text-red-600 hover:text-red-800"
            >
              <XCircle size={16} />
            </button>
          </motion.div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex-1 relative">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Search contact lists..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent bg-white min-w-[130px]"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader
                size={32}
                className="animate-spin text-[#25D366] mx-auto mb-3"
              />
              <p className="text-sm text-gray-600">Loading contact lists...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && segments.length === 0 && (
          <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FileText className="text-gray-400" size={24} />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1.5">
                No contact lists yet
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Create your first contact list to start organizing contacts
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-semibold rounded-lg transition-all"
              >
                <Plus size={16} />
                Create Your First Contact List
              </button>
            </div>
          </div>
        )}

        {/* Segments Grid */}
        {!isLoading && filteredSegments.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSegments.map((segment) => {
              const statusConfig = getStatusBadge(segment.status);
              const StatusIcon = statusConfig.icon;

              return (
                <motion.div
                  key={segment.segment_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                        {segment.segment_name}
                      </h3>
                      {segment.description && (
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {segment.description}
                        </p>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusConfig.class}`}
                    >
                      <StatusIcon size={12} />
                      {statusConfig.label}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Users size={14} />
                      <span className="font-medium">
                        {segment.contact_count || 0} contacts
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(segment.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleViewContacts(segment)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Eye size={14} />
                      View
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSegment(segment);
                        setShowUploadModal(true);
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      <Upload size={14} />
                      Upload
                    </button>
                    <button
                      onClick={() => handleDownloadContacts(segment)}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Download contact list as CSV"
                    >
                      <Download size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteSegment(segment.segment_id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete contact list"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">
                Create Contact List
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({ segment_name: "", description: "" });
                  setCsvFile(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateSegment}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  List Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.segment_name}
                  onChange={(e) =>
                    setFormData({ ...formData, segment_name: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                  placeholder="Enter contact list name"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                  placeholder="Enter description (optional)"
                />
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Upload Contacts (CSV)
                  </label>
                  <button
                    type="button"
                    onClick={downloadSampleCSV}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Download size={14} />
                    Download Sample CSV
                  </button>
                </div>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="csvFileInputCreate"
                  />
                  <label
                    htmlFor="csvFileInputCreate"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="text-gray-400 mb-2" size={24} />
                    <span className="text-sm text-gray-600">
                      {csvFile ? csvFile.name : "Click to select CSV file (optional)"}
                    </span>
                    {csvFile && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCsvFile(null);
                          document.getElementById("csvFileInputCreate").value = "";
                        }}
                        className="mt-2 text-xs text-red-600 hover:text-red-700"
                      >
                        Remove file
                      </button>
                    )}
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  CSV should have columns: contact_name, email, phone (at least phone or email is required)
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ segment_name: "", description: "" });
                    setCsvFile(null);
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#25D366] hover:bg-[#20BD5A] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Creating..." : "Create List"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Upload Modal - For existing segments */}
      {showUploadModal && selectedSegment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">
                Upload Contacts
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setCsvFile(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Upload CSV file for: <strong>{selectedSegment.segment_name}</strong>
              </p>
              <div className="flex items-center justify-end mb-2">
                <button
                  type="button"
                  onClick={downloadSampleCSV}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  <Download size={14} />
                  Download Sample CSV
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                CSV should have columns: contact_name, email, phone (at least phone or email is required)
              </p>
            </div>

            <form onSubmit={handleUploadContacts}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  CSV File <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="csvFileInput"
                  />
                  <label
                    htmlFor="csvFileInput"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="text-gray-400 mb-2" size={24} />
                    <span className="text-sm text-gray-600">
                      {csvFile ? csvFile.name : "Click to select CSV file"}
                    </span>
                    {csvFile && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCsvFile(null);
                          document.getElementById("csvFileInput").value = "";
                        }}
                        className="mt-2 text-xs text-red-600 hover:text-red-700"
                      >
                        Remove file
                      </button>
                    )}
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setCsvFile(null);
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !csvFile}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#25D366] hover:bg-[#20BD5A] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Uploading..." : "Upload"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Contacts View Modal */}
      {showContactsModal && selectedSegment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {selectedSegment.segment_name}
                </h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  {contacts.length} contacts
                </p>
              </div>
              <div className="flex items-center gap-2">
                {contacts.length > 0 && (
                  <button
                    onClick={() => handleDownloadContacts(selectedSegment)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                    title="Download contacts as CSV"
                  >
                    <Download size={16} />
                    Download CSV
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowContactsModal(false);
                    setSelectedSegment(null);
                    setContacts([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {isLoadingContacts ? (
                <div className="flex items-center justify-center py-12">
                  <Loader
                    size={32}
                    className="animate-spin text-[#25D366] mx-auto"
                  />
                </div>
              ) : contacts.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="text-gray-400 mx-auto mb-3" size={48} />
                  <p className="text-sm text-gray-600">No contacts found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                          Name
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                          Email
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                          Phone
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                          WhatsApp Status
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {contacts.map((contact) => {
                        const statusConfig = getStatusBadge(contact.status);
                        const StatusIcon = statusConfig.icon;

                        // Map status to validation status for display
                        // status: 'active' = valid/pending, 'inactive' = invalid/detective
                        // We need to check if we have more info, but for now use status
                        const getValidationBadge = (status, phone) => {
                          // If status is inactive, it's either invalid or detective
                          // If status is active, it could be valid or pending
                          // For now, we'll show based on status only
                          if (status === "inactive") {
                            return {
                              icon: XCircle,
                              label: "Invalid",
                              class: "bg-red-50 text-red-700 border-red-200",
                            };
                          } else if (status === "active") {
                            // Active could mean valid or pending - default to valid if has phone
                            return {
                              icon: CheckCircle,
                              label: phone ? "Valid" : "Pending",
                              class: "bg-green-50 text-green-700 border-green-200",
                            };
                          } else {
                            return {
                              icon: Clock,
                              label: "Pending",
                              class: "bg-yellow-50 text-yellow-700 border-yellow-200",
                            };
                          }
                        };
                        const validationConfig = getValidationBadge(contact.status, contact.phone);
                        const ValidationIcon = validationConfig.icon;

                        return (
                          <tr
                            key={contact.contact_id}
                            className="hover:bg-gray-50 transition"
                          >
                            <td className="px-3 py-3 text-sm text-gray-900">
                              {contact.contact_name || "N/A"}
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-600">
                              {contact.email || "N/A"}
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-600">
                              {contact.phone || "N/A"}
                            </td>
                            <td className="px-3 py-3">
                              {contact.phone ? (
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${validationConfig.class}`}
                                  title={
                                    contact.status === "inactive"
                                      ? "Invalid phone number or not registered on WhatsApp"
                                      : contact.status === "active"
                                      ? "Valid WhatsApp number"
                                      : "Validation pending"
                                  }
                                >
                                  <ValidationIcon size={12} />
                                  {validationConfig.label}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">N/A</span>
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusConfig.class}`}
                              >
                                <StatusIcon size={12} />
                                {statusConfig.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ContactLists;

